/**
 * PublicContentAccess Page
 *
 * Landing page for click-through URLs of the form /p/content?access=:token
 * These tokens are short-lived JWTs minted by the resolve endpoint (Task 5) and
 * embedded as tile links by the ContentPick / ViewContentQuery renderers (Tasks 13/14).
 *
 * Flow:
 *   1. Extract `access` query-param
 *   2. Call publicLinksService.getPublicContent(accessToken)
 *   3a. If type === 'view' → mount PageBuilderWrapper (recursive / nested view render)
 *   3b. Otherwise          → mount PublicContentRender (file viewer + download)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Box, CircularProgress, Typography, Container, Paper } from '@mui/material';
import { publicLinksService } from '../services/public-links/public-links.service';
import { PublicContentRender } from '../components/public/PublicContentRender';
import { PublicLinkAccessContext } from '../context/PublicLinkAccessContext';
import { PageBuilderWrapper } from '../components/page-builder/PageBuilderWrapper';
import { StreamingMetadata } from '../types/viewer.types';

/**
 * The outer chrome shared with PublicLinkAccess: a centered Container, the
 * "Tier One Content" branding header that links home, and an elevation-3 Paper
 * wrapping the body. Keeping it inline here (rather than a separate component)
 * matches the colocated style PublicLinkAccess uses; if a third public viewer
 * appears, lift this into a shared `PublicAccessShell` component.
 */
const PublicAccessShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
    <Box
      component={Link}
      to="/"
      sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': { opacity: 0.8 },
      }}
    >
      <Box component="img" src="/apple-touch-icon.png" alt="Tier One Content" sx={{ width: 40, height: 40 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
        Tier One Content
      </Typography>
    </Box>
    <Paper elevation={3} sx={{ overflow: 'hidden' }}>{children}</Paper>
  </Container>
);

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Mirror of the helper in PublicLinkAccess — maps MIME + filename to a viewer content type. */
const getContentTypeFromMime = (mimeType: string, fileName?: string): StreamingMetadata['contentType'] => {
  if (!mimeType) return 'unknown';

  if (fileName) {
    const ext = fileName.toLowerCase().split('.').pop();
    const codeExtensions = [
      'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs', 'go', 'rs',
      'rb', 'php', 'swift', 'kt', 'scala', 'r', 'lua', 'sh', 'bash', 'ps1',
      'dockerfile', 'makefile', 'yml', 'yaml', 'json', 'xml', 'sql', 'proto',
    ];
    if (codeExtensions.includes(ext || '')) return 'code';
    if (ext === 'html' || ext === 'htm') return 'html';
    if (ext === 'md' || ext === 'markdown') return 'markdown';
  }

  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'document';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'text/html') return 'html';
  if (mimeType === 'text/markdown' || mimeType === 'text/x-markdown') return 'markdown';
  if (mimeType === 'application/javascript' || mimeType === 'application/x-javascript') return 'code';
  if (mimeType === 'application/json') return 'code';
  if (mimeType === 'application/xml' || mimeType === 'text/xml') return 'code';
  if (mimeType.startsWith('text/x-')) return 'code';
  if (mimeType.startsWith('text/')) return 'text';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'document';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'document';
  if (mimeType.includes('opendocument')) return 'document';

  return 'unknown';
};

// ── Page component ────────────────────────────────────────────────────────────

export const PublicContentAccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('access') || '';

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<any>(null);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  // Signed download URL fetched lazily (only for file content, not views).
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string | null>(null);
  const [fetchingUrl, setFetchingUrl] = useState(false);

  // ── Load content via access token ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!accessToken) {
        setError({ title: 'Invalid Link', message: 'This link is missing an access token.' });
        setLoading(false);
        return;
      }

      try {
        const data = await publicLinksService.getPublicContent(accessToken);
        if (cancelled) return;
        setContent(data);
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;

        const reason = err?.response?.data?.error?.reason || err?.reason;
        let title = 'Unable to Load Content';
        let message =
          err?.response?.data?.error?.message || err?.message || 'Something went wrong.';

        if (reason === 'TOKEN_EXPIRED' || reason === 'INVALID_TOKEN') {
          title = 'Link Expired';
          message =
            'This content link has expired. Please open the original share to access this content.';
        } else if (
          reason === 'LINK_REVOKED' ||
          reason === 'LINK_EXPIRED' ||
          reason === 'LINK_INVALID'
        ) {
          title = 'Share Unavailable';
          message = 'This share has expired or been revoked.';
        } else if (
          reason === 'CONTENT_NOT_PUBLIC' ||
          reason === 'CONTENT_NOT_REACHABLE'
        ) {
          title = 'Content Unavailable';
          message = 'This content is no longer available through this share.';
        }

        setError({ title, message });
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // ── Fetch signed download URL once content is loaded (file branch only) ────
  useEffect(() => {
    if (!content || content.type === 'view' || fetchingUrl || signedDownloadUrl) return;

    const fetchUrl = async () => {
      setFetchingUrl(true);
      try {
        const dl = await publicLinksService.getPublicContentDownload(accessToken);
        setSignedDownloadUrl(dl.downloadUrl);
      } catch (err) {
        console.error('Failed to fetch signed download URL:', err);
      } finally {
        setFetchingUrl(false);
      }
    };

    fetchUrl();
  }, [content, accessToken]);

  // ── Build StreamingMetadata from the payload (mirrors PublicLinkAccess) ─────
  const streamingMetadata = useMemo((): StreamingMetadata | undefined => {
    if (!content || !signedDownloadUrl) return undefined;

    const mimeType = content.mimeType || content.mime_type || 'application/octet-stream';
    const fileName = content.fileName || content.title || 'download';
    const contentType = getContentTypeFromMime(mimeType, fileName);
    const canConvert = contentType === 'document' && mimeType !== 'application/pdf';

    return {
      contentId: content.itemId || content.id,
      fileName,
      mimeType,
      fileSize: content.fileSize || content.file_size || 0,
      contentType,
      canStream: true,
      canConvert,
      availableFormats: [],
      presignedUrl: signedDownloadUrl,
      downloadStrategy: 'stream',
      downloadMethod: 'single',
      supportsRange: true,
      isLargeFile: (content.fileSize || content.file_size || 0) > 100 * 1024 * 1024,
    };
  }, [content, signedDownloadUrl]);

  // ── Download handler ────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!accessToken) return;
    try {
      const dl = await publicLinksService.getPublicContentDownload(accessToken);
      window.open(dl.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  // ── Render: loading ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ── Render: error ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" gutterBottom>
          {error.title}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
          {error.message}
        </Typography>
      </Box>
    );
  }

  if (!content) return null;

  // ── Render: view branch (recursive) ────────────────────────────────────────
  // Mount PageBuilderWrapper with the nested view's pageDoc, threading the
  // visitor's access token down through PublicLinkAccessContext. Embedded
  // ContentPick / ViewContentQuery blocks inside the nested view detect the
  // accessToken credential and call resolveComponent in nested-auth mode,
  // which lets the server walk THIS view's pageDoc (rather than the top-level
  // share's) and mint new tile tokens carrying parentViewId.
  if (content.type === 'view' && content.pageDoc) {
    // Decode the access token's linkId claim so embedded blocks can build the
    // resolveComponent URL. JWT bodies are base64url-encoded (not encrypted);
    // pulling the linkId out here is intentional and required.
    let nestedLinkId = '';
    try {
      const payload = JSON.parse(
        atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      nestedLinkId = typeof payload?.linkId === 'string' ? payload.linkId : '';
    } catch {
      // Malformed token — embedded blocks will get a null context and skip
      // public-mode resolution. Better than misrouting to a wrong link.
    }

    const nestedCredentials = nestedLinkId
      ? { linkId: nestedLinkId, accessToken }
      : null;

    return (
      <PublicAccessShell>
        <PublicLinkAccessContext.Provider value={nestedCredentials}>
          <PageBuilderWrapper
            mode="view"
            initialPageDoc={content.pageDoc}
            teamId={content.teamId || content.team_id || ''}
            organizationId={content.organizationId || ''}
          />
        </PublicLinkAccessContext.Provider>
      </PublicAccessShell>
    );
  }

  // ── Render: file content branch ─────────────────────────────────────────────
  // Reuse PublicContentRender (Task 15). The `publicLinkCredentials` prop is
  // only used to thread authentication through to UniversalFileViewer for
  // streaming; the access token here is not a linkId/token pair so we pass an
  // empty-credential shape as an accepted fallback.
  return (
    <PublicAccessShell>
      <PublicContentRender
        content={{
          id: content.itemId || content.id,
          title: content.title,
          description: content.description,
          mimeType: content.mimeType || content.mime_type,
          fileSize: content.fileSize || content.file_size,
          teamId: content.teamId || content.team_id,
        }}
        streamingMetadata={streamingMetadata}
        signedDownloadUrl={signedDownloadUrl}
        publicLinkCredentials={{ linkId: '', token: '', password: undefined }}
        fetchingUrl={fetchingUrl}
        onDownload={handleDownload}
        onError={(err) => console.error('Viewer error', err)}
      />
    </PublicAccessShell>
  );
};

export default PublicContentAccess;
